import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit, output, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { getIntegrationImageLogoPath, IntegrationTypes } from '@phoenix/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { RippleModule } from 'primeng/ripple';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TruncatePipe } from '../../../@core/pipes';
import { LocaleDatePipe } from '../../../shared/pipes/locale-date.pipe';
import { BaseListCombinedService } from '../../../@core/services/base-list-combined.service';
import { ProductShopIntegrationLink } from '../../../generated/generated-types';
import { extendedProductShopIntegrationLink, isActiveSelectedEvent } from './types-interfaces';

interface ViewModelType {
  entityId: string;
  productShopIntegrationLink: extendedProductShopIntegrationLink;
  isActive: boolean;
  isLoading: boolean;
  integrationTooltip?: string;
  imageLogoUrl: string;
}

@Component({
  selector: 'app-shop-integration-card',
  templateUrl: './shop-integration-card.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    ToggleSwitch,
    TooltipModule,
    TruncatePipe,
    RippleModule,
    LocaleDatePipe,
  ]
})
export class ShopIntegrationCardComponent implements OnInit {
  baseServices = inject(BaseListCombinedService);

  entityId = input.required<string>();

  productShopIntegrationLink = model.required<extendedProductShopIntegrationLink>();

  isActive = signal<boolean>(false);

  isActiveSelected = output<isActiveSelectedEvent>();
  syncShopIntegration = output<{ shopIntegrationLink: ProductShopIntegrationLink }>();

  isLoading = input<boolean>();

  // Please Only use in Template
  readonly viewModel = computed<ViewModelType>(() => ({
    entityId: this.entityId(),
    productShopIntegrationLink: this.productShopIntegrationLink(),
    isActive: this.isActive(),
    isLoading: this.isLoading(),
    imageLogoUrl: this.getImageLogoUrl(this.productShopIntegrationLink().integration.type)
  }));

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * It initializes the active state of the shop integration link.
   */
  ngOnInit(): void {
    this.isActive.set(!this.productShopIntegrationLink().inactive);
  }

  /**
   * Handles the event when the integration toggle switch is changed.
   * @param checkboxChangeEvent The checkbox change event containing the new state.
   */
  onToggleIntegration(checkboxChangeEvent: CheckboxChangeEvent) {
    this.isActiveSelected.emit({ checkBox: checkboxChangeEvent['checked'], shopIntegrationLink: this.productShopIntegrationLink() });
  }

  /**
   * Handles the event when the sync button is clicked.
   * @param shopIntegrationLink The shop integration link to sync.
   */
  onSyncIntegration(shopIntegrationLink: ProductShopIntegrationLink) {
    this.syncShopIntegration.emit({ shopIntegrationLink: shopIntegrationLink });
  }

  /**
   * Returns the tooltip text based on whether the integration is active or inactive.
   * @returns The tooltip text.
   */
  getIntegrationTooltip(): string {
    return this.isActive()
      ? this.baseServices.i18nService.translate(_('common.active'))
      : this.baseServices.i18nService.translate(_('common.inactive'));
  }

  /**
   * Generates the URL for the integration's logo image based on its type.
   * @param type The type of the shop integration.
   * @returns The URL of the logo image.
   */
  getImageLogoUrl(type: string): string {
    return getIntegrationImageLogoPath(type as IntegrationTypes);
  }
}